import React from "react";
import { Link } from "react-router-dom";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { TextField } from "../forms/textField";
import { SelectField } from "../forms/selectField";
import { SwitchField } from "../forms/switchField";
import { Article } from "../../models/responses/article";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Supplier } from "../../models/responses/supplier";

// ============================================
// INTERFACES
// ============================================

export interface EditableArticleModalHandlers {
  onArticleNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCommentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeliveredQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onExternalArticleCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInternalCommentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPurchaseOrderNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPurchasePriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onReviewedQuoteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSalesPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onSupplierChange: (supplierId: string | null) => void;
  onValidatedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface EditableArticleModalProps {
  show: boolean;
  article: Article;
  clientType: ClientType;
  suppliers: Supplier[];
  isExported: boolean;
  isLoadingSuppliers?: boolean;
  handlers: EditableArticleModalHandlers;
  onHide: () => void;
}

// ============================================
// COMPONENT
// ============================================

const EditableArticleModal: React.FC<EditableArticleModalProps> = ({
  show,
  article,
  clientType,
  suppliers,
  isExported,
  isLoadingSuppliers = false,
  handlers,
  onHide,
}) => {
  const isStandard = clientType === ClientType.Standard;
  const isFleet = clientType === ClientType.Fleet;
  const isVerified = article.verified;
  const hasTemplate = !!article.articleTemplateId;

  return (
    <Modal size="xl" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {article.articleNumber
            ? `${article.articleNumber} - ${article.description}`
            : "Ny artikel"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Description */}
          <Row>
            <TextField
              label="Beskrivning"
              value={article.description}
              colSize={12}
              isEditable={!isVerified}
              onChange={handlers.onDescriptionChange}
            />
          </Row>

          {/* Quantities */}
          <Row>
            <TextField
              label="Antal beställda"
              value={article.quantity}
              colSize={2}
              type="number"
              isEditable={!isVerified}
              onChange={handlers.onQuantityChange}
            />
            <TextField
              label="Antal levererade"
              value={article.deliveredQuantity}
              colSize={2}
              type="number"
              isEditable={!isVerified}
              onChange={handlers.onDeliveredQuantityChange}
            />
            {isFleet && (
              <TextField
                label="Lager från AX"
                value={article.stock ?? "Ej hämtat"}
                colSize={2}
                isEditable={false}
              />
            )}
          </Row>

          {/* Supplier & Article Number */}
          <Row>
            <SelectField
              label="Leverantör"
              value={article.supplierId}
              options={suppliers}
              getOptionValue={(s) => s.id}
              getOptionLabel={(s) => s.name}
              colSize={6}
              isEditable={!isVerified && !hasTemplate}
              onChange={(value) => handlers.onSupplierChange(value || null)}
              isLoading={isLoadingSuppliers}
              placeholder="- Ingen leverantör vald -"
            />
            <TextField
              label="Artikelnummer"
              value={article.articleNumber}
              colSize={6}
              isEditable={!isVerified}
              onChange={handlers.onArticleNumberChange}
            />
          </Row>

          {/* Comments */}
          <Row>
            <TextField
              label="Extern kommentar"
              value={article.comment}
              colSize={6}
              isEditable={!isVerified}
              onChange={handlers.onCommentChange}
            />
            <TextField
              label="Intern kommentar"
              value={article.internalComment}
              colSize={6}
              isEditable={!isVerified}
              onChange={handlers.onInternalCommentChange}
            />
          </Row>

          {/* External prefix & Discount */}
          <Row>
            <TextField
              label="Externt prefix"
              value={article.externalArticleCode}
              colSize={6}
              isEditable={!isVerified}
              onChange={handlers.onExternalArticleCodeChange}
            />
            {(isStandard || isFleet) && (
              <TextField
                label="Rabatt (%)"
                value={article.discount}
                colSize={6}
                type="number"
                min={0}
                max={100}
                isEditable={!isVerified}
                onChange={handlers.onDiscountChange}
              />
            )}
          </Row>

          {/* IO-nummer & AX article number */}
          <Row>
            <TextField
              label="IO-nummer"
              value={article.purchaseOrderNumber}
              colSize={6}
              isEditable={!isVerified}
              onChange={handlers.onPurchaseOrderNumberChange}
            />
            <TextField
              label="Artikelnummer i AX"
              value={article.adId}
              colSize={6}
              isEditable={false}
            />
          </Row>

          {/* Prices */}
          <Row>
            <TextField
              label="Inköpspris"
              value={article.purchasePrice}
              colSize={6}
              isEditable={!isVerified && !hasTemplate}
              onChange={handlers.onPurchasePriceChange}
            />
            <TextField
              label="Försäljningspris"
              value={article.salesPrice}
              colSize={6}
              isEditable={!isVerified && !article.isSyncedWithAX}
              onChange={handlers.onSalesPriceChange}
            />
          </Row>

          {/* Calculated prices (read-only) */}
          <Row>
            <TextField
              label="SPN"
              value={article.storeSalesPrice}
              colSize={6}
              isEditable={false}
            />
            <TextField
              label="VSN"
              value={article.workshopSalesPrice}
              colSize={6}
              isEditable={false}
            />
          </Row>

          {/* PL URL & Exclude switch */}
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>PL URL</Form.Label>
                <div className="mt-2">
                  <Link target="_blank" className="mt-1" to={article.partUrl ?? ""}>
                    {article.partUrl ?? ""}
                  </Link>
                </div>
              </Form.Group>
            </Col>
            <SwitchField
              label="Exkluderad från order"
              checked={article.excludeFromWarehousePicking || false}
              isEditable={false}
            />
          </Row>

          <hr />

          {/* Total */}
          <Row>
            <TextField
              label="Totalt exkl. moms"
              value={article.totalExVat}
              colSize={4}
              isEditable={false}
            />
            <TextField
              label="Totalt inkl. moms"
              value={article.totalIncVat}
              colSize={4}
              isEditable={false}
            />
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Container>
          <div className="float-start">
            <Button className="mb-3" variant="secondary" onClick={handlers.onRemove}>
              Ta bort artikel
            </Button>
          </div>

          <div className="float-end d-inline-flex">
            <div className="me-3">
              <Form.Check
                type="switch"
                label="Granskad offert"
                className="mt-2"
                onChange={handlers.onReviewedQuoteChange}
                checked={article.reviewedQuote}
                disabled={isExported}
                readOnly={isExported}
              />
            </div>
            <div className="me-3">
              <Form.Check
                type="switch"
                label="Klarmarkerad"
                className="mt-2"
                onChange={handlers.onValidatedChange}
                checked={article.verified}
                disabled={isExported}
                readOnly={isExported}
              />
            </div>
            <div>
              <Button className="ms-3 mb-3" variant="primary" onClick={handlers.onSave}>
                Spara ändringar
              </Button>
            </div>
          </div>
        </Container>
      </Modal.Footer>
    </Modal>
  );
};

export default EditableArticleModal;
